import { useCallback, useMemo } from 'react';

import { readSendIncomplete, readUserInputMessage } from '../../lib/helpers/graphql-error.helpers';
import { pluralize } from '../../lib/helpers/text.helpers';
import { useIntlSnackbar } from '../../lib/hooks/use-intl-snackbar';
import { useIsMounted } from '../../lib/hooks/use-is-mounted';
import { useOperationErrorHandler } from '../../lib/hooks/use-operation-error-handler';
import {
  buildSendReportCards,
  buildSetAgeGroupIncludeNonConcluded,
  useReportCardSendPreview,
} from '../../lib/services/report-card-access.service';
import { OUTCOME_SNACKBAR_MS } from '../../lib/snackbar.constants';
import { useCompanySportTerminology } from '../../lib/stores/company.store';
import { ageGroupSendReports, athleteNouns, sendPreviewOutcome } from './report-card-access.helpers';
import { ReportCardAvailabilityMessages } from './report-card-access.types';
import { useAgeGroupWriteFlight } from './report-card-age-group.use-write-flight';

interface IUseSendReportCardsArgs {
  ageGroup: IAgeGroup;
  availability: IReportCardAvailability;
  open: boolean;
  messages: ReportCardAvailabilityMessages;
  onSent: () => void;
}

// The send dialog's two writes: the age group's include-non-concluded choice (a failed save reverts) and
// the send itself, which passes the value the preview was built with and reports what the send did.
export const useSendReportCards = ({ ageGroup, availability, open, messages, onSent }: IUseSendReportCardsArgs) => {
  const showSnackbar = useIntlSnackbar();
  const reportFailure = useOperationErrorHandler(messages.errSendFailed);
  const isMounted = useIsMounted();
  const termLookup = useCompanySportTerminology();
  const nouns = useMemo(() => athleteNouns(termLookup), [termLookup]);
  const values = useMemo(() => ({ ageGroup: ageGroup.name }), [ageGroup.name]);

  const { includeNonConcluded } = availability;
  const { preview, loading, error, refetch } = useReportCardSendPreview({ ageGroup, includeNonConcluded, skip: !open });
  const outcome = preview && !error ? sendPreviewOutcome(preview, ageGroup._id, includeNonConcluded) : null;

  const { setAgeGroupIncludeNonConcluded } = buildSetAgeGroupIncludeNonConcluded();
  const saveSetting = useCallback(async (value: boolean | null) => {
    try {
      await setAgeGroupIncludeNonConcluded(availability, value);
    } catch (saveError) {
      const reason = readUserInputMessage(saveError);
      reportFailure('save include non-concluded', saveError, {
        message: reason ? messages.errSettingRefused : messages.errSettingFailed,
        values: { ...values, reason },
      });
    }
  }, [availability, values, messages, reportFailure, setAgeGroupIncludeNonConcluded]);
  const setting = useAgeGroupWriteFlight(ageGroup._id, 'SETTING', saveSetting);

  const { sendReportCards } = buildSendReportCards();
  const canSend = outcome === 'READY' && !loading;
  const send = useCallback(async () => {
    if (!canSend) return;
    try {
      const result = await sendReportCards({ assessmentId: ageGroup.assessmentId, ageGroupIds: [ageGroup._id], includeNonConcluded });
      ageGroupSendReports(messages, result, { ageGroup: ageGroup.name, nouns })
        .forEach(({ message, variant, values: counts }) => showSnackbar(message, variant, OUTCOME_SNACKBAR_MS, counts));
      if (isMounted()) onSent();
    } catch (sendError) {
      const incomplete = readSendIncomplete(sendError);
      const reason = readUserInputMessage(sendError);
      if (incomplete) {
        const total = incomplete.sent + incomplete.failed;
        reportFailure('send report cards', sendError, {
          message: messages.errSendIncomplete,
          values: { ...values, failed: incomplete.failed, total, players: pluralize(total, nouns.one, nouns.many) },
        });
      } else {
        reportFailure('send report cards', sendError, {
          message: reason ? messages.errSendRefused : messages.errSendFailed,
          values: { ...values, reason },
        });
      }
      // A refusal (e.g. access turned off meanwhile) and the players left to finish show up in a fresh preview.
      if (isMounted()) await refetch().catch(refetchError => console.warn('reportCardSendPreview refetch failed:', refetchError));
    }
  }, [canSend, ageGroup._id, ageGroup.assessmentId, ageGroup.name, includeNonConcluded, values, nouns, messages, sendReportCards, showSnackbar, reportFailure, isMounted, onSent, refetch]);
  const sending = useAgeGroupWriteFlight(ageGroup._id, 'SEND', send);

  const retryPreview = useCallback(() => {
    refetch().catch(refetchError => console.warn('reportCardSendPreview retry failed:', refetchError));
  }, [refetch]);

  return {
    preview,
    previewLoading: loading,
    previewError: !!error,
    outcome,
    retryPreview,
    saveSetting: setting.run,
    settingInFlight: setting.inFlight,
    send: sending.run,
    sendInFlight: sending.inFlight,
    canSend: canSend && !sending.busy,
    busy: sending.busy,
  };
};
